import { get } from 'lodash-es';
import type {
  ConditionalActionValueType,
  DataPoint,
  DataPoints,
} from '@web-config-app/core';
import type { ConditionalActionConditionSubject } from './types';

/**
 * Resolving a condition subject against the item it targets — the enclosing
 * item for `type: currentArrayItem` subjects, the referenced item found via
 * `findReferencedItem` for `type: dataSource` / `dataPoint`. A subject's
 * value type and its comparison-value options are intrinsic properties of
 * that item, read via the subject's relative paths — NOT registry data
 * sources. These are pure functions of `(resolutionData, subject)`; the
 * control resolves the item once and hands it down.
 */

export interface CatalogOption {
  value: string;
  label: string;
}

/**
 * The logical `ConditionalActionValueType` a subject produces — drives which
 * operators apply. Read the subject's `valueTypeFrom.path` off the resolution
 * item (e.g. `answerType`) and look the result up in `valueTypeFrom.mapping`.
 *
 * Returns `undefined` when the subject is missing, the resolution item isn't
 * loaded, or the read value isn't a key in the mapping table.
 */
export const resolveSubjectValueType = (
  resolutionData: unknown,
  subject: ConditionalActionConditionSubject | undefined,
): ConditionalActionValueType | undefined => {
  if (!subject?.valueTypeFrom) return undefined;
  const { path, mapping } = subject.valueTypeFrom;
  const rawKey = get(resolutionData, path);
  const key = typeof rawKey === 'string' ? rawKey : undefined;
  return key ? mapping[key] : undefined;
};

/**
 * The comparison-value picker's options for a subject — the array at the
 * subject's `valueCatalog.path` on the resolution item, mapped into
 * `{ value, label }`. Returns `[]` when the subject declares no catalog
 * (or no `mapping`), the resolution item isn't loaded, or the path doesn't
 * resolve to an array. When `optionLabelPath` is omitted the value doubles
 * as the label. Items whose mapped value is empty are dropped.
 */
export const resolveCatalogOptions = (
  resolutionData: unknown,
  subject: ConditionalActionConditionSubject | undefined,
): CatalogOption[] => {
  const catalog = subject?.valueCatalog;
  if (!catalog?.mapping || !resolutionData) return [];
  const { optionValuePath, optionLabelPath } = catalog.mapping;
  const raw = get(resolutionData, catalog.path);
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      const rawValue = get(item, optionValuePath);
      const rawLabel = optionLabelPath ? get(item, optionLabelPath) : undefined;
      const value = rawValue == null ? '' : String(rawValue);
      const label = rawLabel == null ? value : String(rawLabel);
      return { value, label };
    })
    .filter((option) => option.value !== '');
};

/**
 * Find the referenced item for a `dataSource` / `dataPoint` subject — the
 * object in the entity data whose value at `valuePath` equals `value` (e.g.
 * the question whose `linkId` matches a condition's `ref`). The subject's
 * `valueCatalog` / `valueTypeFrom` then resolve against that item exactly
 * as they do against the enclosing item for an `currentArrayItem` subject.
 *
 * Reads from the (reactive) entity data rather than the data source's
 * registry value, so the resolved subject stays in sync as questions change
 * — `useDataSource` returns a non-reactive ref, so reading its value here
 * would go stale. Returns `undefined` when nothing matches.
 *
 * The `requiredPaths` shape gate stops the DFS from returning a shallow
 * `{ [valuePath]: value }` node living deeper in entityData — e.g. a `goTo`
 * action payload `{ linkId: "..." }`. That's why no separate `scopePath`
 * scoping is needed: the gate rejects the shallow shadow directly.
 */
/**
 * Flatten the grouped data-point tree (`useDataPoints` returns flow-scoped
 * groups whose leaves are the selectable points) into just the selectable
 * leaves — a leaf is a data point with no children. These become the
 * `dataPoint` subject's picker options and the resolution items its value
 * type is read off. Already filtered by the subject's `dataTypes` upstream.
 */
export const flattenDataPointLeaves = (flows: DataPoints): DataPoint[] => {
  const leaves: DataPoint[] = [];
  const visit = (points: DataPoint[] | undefined) => {
    if (!points) return;
    points.forEach((point) => {
      // A `dataPoints` array (even an empty one) marks a group container, not
      // a selectable leaf — a group whose leaves were all filtered out
      // contributes nothing rather than surfacing itself as a false leaf.
      if (Array.isArray(point.dataPoints)) {
        visit(point.dataPoints);
      } else {
        leaves.push(point);
      }
    });
  };
  flows.forEach((flow) => visit(flow.dataPoints));
  return leaves;
};

export const findReferencedItem = (
  entityData: unknown,
  valuePath: string,
  value: string,
  requiredPaths: string[] = [],
): unknown => {
  // Shape gate: a real referenced item carries at least one of the subject's
  // declared resolution paths (e.g. `answerType`, `choiceOptions.answerOptions`).
  // Empty `requiredPaths` disables the gate.
  const matchesShape = (node: unknown): boolean =>
    requiredPaths.length === 0 ||
    requiredPaths.some((p) => get(node, p) !== undefined);

  let found: unknown;
  const visit = (node: unknown): boolean => {
    if (node === null || typeof node !== 'object') return false;
    if (String(get(node, valuePath)) === value && matchesShape(node)) {
      found = node;
      return true;
    }
    return Object.values(node).some(visit);
  };
  visit(entityData);
  return found;
};
